import { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from './ui/drawer';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X } from 'lucide-react';
import type { ExtendedUnitFilters } from '@/lib/units';
import { getUnitsCount } from '@/lib/units';
import { formatFloorDisplay } from '@/lib/utils';

interface ResidencesAdvancedFiltersProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: ExtendedUnitFilters;
  onFiltersChange: (filters: ExtendedUnitFilters) => void;
  onApply: () => void;
  onReset: () => void;
  priceRange?: { min: number; max: number };
  areaRange?: { min: number; max: number };
}

export const ResidencesAdvancedFilters = ({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  priceRange = { min: 0, max: 2000000 },
  areaRange = { min: 0, max: 3000 },
}: ResidencesAdvancedFiltersProps) => {
  const [localFilters, setLocalFilters] = useState<ExtendedUnitFilters>(filters);
  const [floorCounts, setFloorCounts] = useState<Record<string, number>>({});

  // Load floor counts (available units only)
  useEffect(() => {
    const loadFloorCounts = async () => {
      const [gfCount, secondCount, thirdCount, fourthCount] = await Promise.all([
        getUnitsCount({ floor: 'GF', availability: 'available' }),
        getUnitsCount({ floor: 'second', availability: 'available' }),
        getUnitsCount({ floor: 'third', availability: 'available' }),
        getUnitsCount({ floor: 'fourth', availability: 'available' }),
      ]);

      setFloorCounts({
        GF: gfCount.count,
        second: secondCount.count,
        third: thirdCount.count,
        fourth: fourthCount.count,
      });
    };

    if (open) {
      loadFloorCounts();
    }
  }, [open]);

  const handleFilterChange = <K extends keyof ExtendedUnitFilters>(
    key: K,
    value: ExtendedUnitFilters[K]
  ) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onApply();
    onOpenChange(false);
  };

  const handleReset = () => {
    const resetFilters: ExtendedUnitFilters = {};
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
    onReset();
    onOpenChange(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.viewType) count++;
    if (localFilters.availability) count++;
    if (localFilters.category) count++;
    if (localFilters.block) count++;
    if (localFilters.floor) count++;
    if (localFilters.bedrooms) count++;
    if (localFilters.bathrooms) count++;
    if (localFilters.minPrice || localFilters.maxPrice) count++;
    if (localFilters.minArea || localFilters.maxArea) count++;
    return count;
  };

  const activeCount = getActiveFilterCount();

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader>
          <div className="flex items-center justify-between">
            <div>
              <DrawerTitle className="text-2xl">Advanced Filters</DrawerTitle>
              <DrawerDescription>
                Refine your search with detailed filters
              </DrawerDescription>
            </div>
            {activeCount > 0 && (
              <Badge variant="default" className="bg-brand-teal text-white">
                {activeCount} active
              </Badge>
            )}
          </div>
        </DrawerHeader>

        <div className="overflow-y-auto px-4 pb-4 space-y-6">
          {/* View Type */}
          <div>
            <Label>View Type</Label>
            <Select
              value={localFilters.viewType || 'all'}
              onValueChange={(value) => {
                const typedValue = value === 'all' ? undefined : (value as 'lagoon' | 'canal');
                handleFilterChange('viewType', typedValue);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Views</SelectItem>
                <SelectItem value="lagoon">Lagoon View</SelectItem>
                <SelectItem value="canal">Canal View</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Availability */}
          <div>
            <Label>Availability</Label>
            <Select
              value={localFilters.availability || 'all'}
              onValueChange={(value) => {
                const typedValue = value === 'all' ? undefined : (value as 'available' | 'reserved' | 'sold');
                handleFilterChange('availability', typedValue);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="reserved">Reserved</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div>
            <Label>Category</Label>
            <Select
              value={localFilters.category || 'all'}
              onValueChange={(value) => {
                const typedValue = value === 'all' ? undefined : (value as '1bed' | '2bed' | 'townhouse');
                handleFilterChange('category', typedValue);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="1bed">1 Bedroom</SelectItem>
                <SelectItem value="2bed">2 Bedroom</SelectItem>
                <SelectItem value="townhouse">Townhouse</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* View */}
          <div>
            <Label>View</Label>
            <Select
              value={localFilters.block || 'all'}
              onValueChange={(value) => handleFilterChange('block', value === 'all' ? undefined : value as 'A' | 'B')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Views</SelectItem>
                <SelectItem value="A">Canal View</SelectItem>
                <SelectItem value="B">Lagoon View</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Floor */}
          <div>
            <Label>Floor</Label>
            <Select
              value={localFilters.floor || 'all'}
              onValueChange={(value) => handleFilterChange('floor', value === 'all' ? undefined : value as 'GF' | 'second' | 'third' | 'fourth')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Floors</SelectItem>
                <SelectItem value="GF">
                  GF
                  {floorCounts.GF !== undefined && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({floorCounts.GF} available)
                    </span>
                  )}
                </SelectItem>
                <SelectItem value="second">
                  2F
                  {floorCounts.second !== undefined && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({floorCounts.second} available)
                    </span>
                  )}
                </SelectItem>
                <SelectItem value="third">
                  3F
                  {floorCounts.third !== undefined && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({floorCounts.third} available)
                    </span>
                  )}
                </SelectItem>
                <SelectItem value="fourth">
                  4F / Penthouse
                  {floorCounts.fourth !== undefined && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({floorCounts.fourth} available)
                    </span>
                  )}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bedrooms */}
          <div>
            <Label>Bedrooms</Label>
            <Select
              value={localFilters.bedrooms?.toString() || 'all'}
              onValueChange={(value) => handleFilterChange('bedrooms', value === 'all' ? undefined : parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bathrooms */}
          <div>
            <Label>Bathrooms</Label>
            <Select
              value={localFilters.bathrooms?.toString() || 'all'}
              onValueChange={(value) => handleFilterChange('bathrooms', value === 'all' ? undefined : parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Price Range */}
          <div>
            <Label>Price Range</Label>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={localFilters.minPrice || ''}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Max"
                    value={localFilters.maxPrice || ''}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Area Range */}
          <div>
            <Label>Area Range (sq.ft)</Label>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={localFilters.minArea || ''}
                    onChange={(e) => handleFilterChange('minArea', e.target.value ? parseFloat(e.target.value) : undefined)}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Max"
                    value={localFilters.maxArea || ''}
                    onChange={(e) => handleFilterChange('maxArea', e.target.value ? parseFloat(e.target.value) : undefined)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sort By */}
          <div>
            <Label>Sort By</Label>
            <Select
              value={localFilters.sortBy || 'unit_number'}
              onValueChange={(value) => handleFilterChange('sortBy', value as ExtendedUnitFilters['sortBy'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unit_number">Unit Number</SelectItem>
                <SelectItem value="price">Price</SelectItem>
                <SelectItem value="area_total">Area</SelectItem>
                <SelectItem value="bedrooms">Bedrooms</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort Order */}
          <div>
            <Label>Sort Order</Label>
            <Select
              value={localFilters.sortOrder || 'asc'}
              onValueChange={(value) => handleFilterChange('sortOrder', value as 'asc' | 'desc')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">Ascending</SelectItem>
                <SelectItem value="desc">Descending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

          <DrawerFooter>
            <div className="flex gap-4 w-full">
              <Button variant="outline" onClick={handleReset} className="flex-1">
                <X className="w-4 h-4 mr-2" />
                Reset to Default
              </Button>
              <Button onClick={handleApply} className="flex-1 bg-brand-teal hover:bg-brand-navy text-white">
                Apply Filters
              </Button>
            </div>
          </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

